import { createContext, useContext, useState } from "react"
import { setSnackbarEmitter } from "../lib/snackbar-emitter"

const SnackbarContext = createContext({})
export const useSnackbar = () => useContext(SnackbarContext)

// ── mesmos tipos/cores da resposta anterior ──────────────────────────────────
type Severity = "success" | "error" | "warning" | "info"

const COLORS: Record<Severity, string> = {
  success: "#3da85f",
  error:   "#d94040",
  warning: "#c97b1a",
  info:    "#2a7dc9",
}

const TITLES: Record<Severity, string> = {
  success: "Sucesso!",
  error:   "Erro!",
  warning: "Atenção!",
  info:    "Info",
}

const ICONS: Record<Severity, React.ReactNode> = {
  success: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="8.25" stroke="rgba(255,255,255,0.6)" strokeWidth="1.2"/>
      <path d="M5.5 9.2l2.5 2.5 4.5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  error: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="8.25" stroke="rgba(255,255,255,0.6)" strokeWidth="1.2"/>
      <path d="M6 6l6 6M12 6l-6 6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  ),
  warning: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 2L16.5 15H1.5L9 2z" stroke="rgba(255,255,255,0.6)" strokeWidth="1.2" fill="none"/>
      <path d="M9 7.5v3.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/>
      <circle cx="9" cy="13" r="0.9" fill="#fff"/>
    </svg>
  ),
  info: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="8.25" stroke="rgba(255,255,255,0.6)" strokeWidth="1.2"/>
      <path d="M9 8.5v4" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/>
      <circle cx="9" cy="5.5" r="0.9" fill="#fff"/>
    </svg>
  ),
}

const DURATION = 4000

// ── provider ─────────────────────────────────────────────────────────────────
export const SnackbarProvider = ({ children }:{children: any}) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as Severity,
    key: 0, // força re-mount da barra de progresso em disparos seguidos
  })

  const showSnackbar = ({ message, severity = "success" }: { message: string; severity?: Severity }) => {
    setSnackbar(prev => ({ open: true, message, severity, key: prev.key + 1 }))
  }

  setSnackbarEmitter(showSnackbar)

  const handleClose = () => setSnackbar(prev => ({ ...prev, open: false }))

  const { open, message, severity, key } = snackbar

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}

      {open && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 9999,
          minWidth: 280, maxWidth: 360,
          borderRadius: 6, overflow: "hidden",
          background: COLORS[severity],
          boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
          animation: "toastIn 0.28s cubic-bezier(.22,.61,.36,1) both",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "13px 14px 10px" }}>
            <div style={{ width: 22, height: 22, flexShrink: 0 }}>{ICONS[severity]}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>{TITLES[severity]}</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.88)", marginTop: 1 }}>{message}</div>
            </div>
            <button onClick={handleClose} style={{
              background: "none", border: "none", cursor: "pointer",
              color: "rgba(255,255,255,0.75)", fontSize: 20, lineHeight: 1,
              padding: 0, alignSelf: "flex-start", marginTop: -4,
            }}>×</button>
          </div>

          <div style={{ height: 3, background: "rgba(255,255,255,0.28)" }}>
            <div key={key} style={{
              height: "100%", background: "rgba(255,255,255,0.75)",
              transformOrigin: "left",
              animation: `toastProgress ${DURATION}ms linear forwards`,
            }} onAnimationEnd={handleClose} />
          </div>
        </div>
      )}

      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateX(60px) scale(0.96); }
          to   { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes toastProgress {
          from { transform: scaleX(1); }
          to   { transform: scaleX(0); }
        }
      `}</style>
    </SnackbarContext.Provider>
  )
}